import React, { Suspense, lazy, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import Background from "../components/Background";
import Nav from "../components/Nav";
import Hero from "../components/Hero";
import Section from "../components/Section";
import Footer from "../components/Footer";
import BackgroundThemeContextProvider from "../providers/BackgroundThemeProvider";
import { useHash } from "react-use";
import { ScrollObserver } from "../utils/ScrollObserver";
import scrollTo from "../libs/scrollTo";
import Picture from "../components/Picture";

const Sketch = lazy(() => import("../components/Sketch"));

const observerInstance = new ScrollObserver({
  threshold: [0, 0.5, 1],
});

const CTA_LINK_VARIANTS = {
  "purple-white": "bg-about-purple-cta-gradient text-white",
  "blue-white": "bg-about-blue-cta-gradient text-white",
  glass: "bg-white/[35%] hover:bg-white/[50%] text-white transition-colors",
};

const CTALink = ({ children, className, variant, href }) => {
  const ctaVariant = CTA_LINK_VARIANTS[variant];
  return (
    <a
      href={href}
      className={clsx(
        "[font-size:15px] leading-4 px-10 py-3 rounded-full w-fit mx-auto font-semibold",
        "md:[font-size:18px] md:leading-[18px] md:px-6 md:py-3.5 md:mx-0",
        "lg:[font-size:20px] lg:leading-[20px] lg:p-8 lg:py-4",
        "xl:[font-size:24px] xl:px-10 xl:py-5",
        ctaVariant,
        className
      )}
      rel="noreferrer"
      target="_blank"
    >
      {children}
    </a>
  );
};

function Home() {
  const { t } = useTranslation();
  const [hash] = useHash();

  useEffect(() => {
    if (!hash) return;
    setTimeout(() => {
      scrollTo(null, hash.replace(/^#!/, ""));
    }, 150);
  }, [hash]);

  useLayoutEffect(() => {
    const addedClassNames = ["transition", "duration-700", "ease-out-back"];

    const obseravleNodes = Array.from(
      document.querySelectorAll("[data-observable]")
    ).filter((el) => el.dataset.initialStyles);

    for (let i = obseravleNodes.length; i--; ) {
      const initialStyles = obseravleNodes[i].dataset.initialStyles.split(" ");
      obseravleNodes[i].classList.add(...initialStyles);
    }

    observerInstance.observe({
      elements: obseravleNodes,
      stagger: 100,
      alwaysTrigger: false,
      onElVisible: ({ el }) => {
        const initialStyles = el.dataset.initialStyles.split(" ");
        let initialStylesLength = initialStyles.length;

        el.classList.add(...addedClassNames);

        for (let i = initialStylesLength; i--; ) {
          el.classList.remove(initialStyles[i]);
        }
      },
    });

    return () => {
      observerInstance.unobserveEls(obseravleNodes);
    };
  }, []);

  return (
    <BackgroundThemeContextProvider>
      <div className="cursor-default antialiased font-medium text-white">
        <Background />

        <Suspense>
          <Sketch />
        </Suspense>

        <Nav />

        <div
          className={clsx(
            "relative z-10 px-3.5 space-y-4 w-full",
            "md:px-10 md:space-y-5",
            "lg:px-0 lg:space-y-6 lg:max-w-[904px] lg:mx-auto",
            "xl:max-w-[1160px]",
            "2xl:max-w-[1320px]"
          )}
        >
          <Hero />

          {/* Section 1 */}
          <Section
            nodeIndex={0}
            className={clsx(
              "bg-about-blue-gradient pt-8",
              "md:pt-9 md:flex-row md:pl-10 md:justify-between md:items-start",
              "lg:pt-14 lg:pl-9",
              "xl:pt-[94px] xl:pl-12 xl:pr-4",
              "2xl:pt-[82px] 2xl:pl-[58px]"
            )}
          >
            <Picture
              path="/img/home/1"
              className="absolute inset-0 pointer-events-none"
              alt="Divada and TechV"
              imgClassName="object-cover h-full w-full"
            />
            <div
              className={clsx(
                "flex flex-col mb-3 relative",
                "md:mt-3 md:max-w-[45%]",
                "lg:mt-4",
                "xl:mt-1.5",
                "2xl:max-w-[40%]"
              )}
            >
              <div
                className={clsx(
                  "flex flex-col items-center max-w-[300px] mb-6 mx-auto",
                  "md:items-start md:max-w-fit"
                )}
              >
                <h3
                  className={clsx(
                    "block text-center text-white font-bold [font-size:26px] leading-8",
                    "md:text-left md:[font-size:46px] md:leading-[46px]",
                    "lg:[font-size:60px] lg:leading-[60px]",
                    "xl:[font-size:80px] xl:leading-[80px]"
                  )}
                >
                  {t("chapters.0.title")}
                </h3>
                <p
                  className={clsx(
                    "text-center text-white font-semibold [font-size:26px] leading-[31px]",
                    "md:text-left md:font-bold md:[font-size:46px] md:leading-[46px]",
                    "lg:[font-size:60px] lg:leading-[60px]",
                    "xl:[font-size:80px] xl:leading-[80px]"
                  )}
                >
                  {t("chapters.0.subtitle")}
                </p>
              </div>
              <CTALink href="https://link.numi.net/43wXKot" variant="glass">
                {t("chapters.0.cta")}
              </CTALink>
            </div>
          </Section>

          <div
            className={clsx(
              "flex flex-col space-y-4",
              "md:flex-row md:space-y-0 md:space-x-5",
              "lg:space-x-6"
            )}
          >
            {/* Section 2 */}
            <Section
              nodeIndex={0}
              className={clsx(
                "bg-about-blue-gradient pt-10",
                "md:flex-1 md:pt-10",
                "lg:pt-12.5",
                "xl:pt-15"
              )}
            >
              <Picture
                path="/img/home/2"
                className="absolute inset-0 pointer-events-none"
                imgClassName="object-cover h-full w-full"
              />
              <div
                className={clsx(
                  "mb-12 max-w-[calc(100%-1rem)] relative",
                  "md:mb-4",
                  "lg:mb-7",
                  "xl:mb-10",
                  "2xl:mb-20 2xl:max-w-[416px]"
                )}
              >
                <h3
                  className={clsx(
                    "block text-center text-white font-bold [font-size:26px] leading-[31px]",
                    "md:[font-size:22px] md:leading-[24px]",
                    "lg:[font-size:32px] lg:leading-[35px]",
                    "xl:[font-size:40px] xl:leading-[44px]"
                  )}
                >
                  {t("chapters.1.title")}
                </h3>
                <p
                  className={clsx(
                    "text-center text-white font-semibold [font-size:26px] leading-[31px]",
                    "md:[font-size:22px] md:leading-[24px] md:mb-0",
                    "lg:[font-size:32px] lg:leading-[35px]",
                    "xl:[font-size:40px] xl:leading-[44px] whitespace-pre-wrap"
                  )}
                >
                  {t("chapters.1.subtitle")}
                </p>
              </div>
            </Section>

            {/* Section 3 */}
            <Section
              nodeIndex={0}
              className={clsx(
                "bg-about-blue-gradient pt-8 pb-[340px]",
                "md:flex-[1.43] md:py-10",
                "lg:flex-[1.425] lg:pt-12.5",
                "xl:flex-[1.42] xl:py-[60px]"
              )}
            >
              <Picture
                path="/img/home/3"
                className="absolute inset-0 pointer-events-none"
                imgClassName="object-contain h-full w-full"
              />
              <div
                className={clsx(
                  "relative text-center text-white font-bold [font-size:26px] leading-[31px] max-w-[calc(100%-5.5rem)] mx-auto",
                  "md:[font-size:22px] md:leading-[24px]",
                  "lg:[font-size:32px] lg:leading-[35px]",
                  "xl:[font-size:40px] xl:leading-[44px] xl:max-w-[calc(100%-7.5rem)]",
                  "2xl:max-w-[500px]"
                )}
              >
                <h3 className="font-bold">{t("chapters.2.title")}</h3>
                <p className="font-semibold md:mb-0">
                  {t("chapters.2.subtitle")}
                </p>
              </div>
            </Section>
          </div>

          {/* Section 4 */}
          <Section
            nodeIndex={0}
            className={clsx(
              "px-7.5 py-40.5 bg-white",
              "md:py-29.5 md:px-8.5",
              "lg:py-42 lg:px-13.5",
              "xl:py-56 xl:px-10",
              "2xl:py-68.5"
            )}
          >
            <h3
              className={clsx(
                "mb-6 gradient-text-blue-gradient",
                "[font-size:35px] leading-[35px] font-bold text-center",
                "md:[font-size:46px] md:leading-[46px]",
                "lg:[font-size:60px] lg:leading-[60px] lg:mb-8",
                "xl:[font-size:80px] xl:leading-[80px]",
                "2xl:max-w-[1080px]"
              )}
            >
              {t("chapters.3.title")}
            </h3>
            <CTALink href="https://link.numi.net/43wXKot" variant="blue-white">
              {t("chapters.3.cta")}
            </CTALink>
          </Section>

          {/* Section 5 */}
          <Section
            nodeIndex={1}
            className={clsx(
              "bg-about-purple-gradient px-5 pt-8 pb-1",
              "md:flex-row md:pl-10 md:pr-0 md:pt-[50px] md:pb-5 md:justify-between",
              "lg:pt-18.5 lg:pl-9 lg:pr-3 lg:pb-10",
              "xl:pt-25 xl:pb-15 xl:pr-5 xl:pl-12",
              "2xl:py-27"
            )}
          >
            <Picture
              path="/img/home/5"
              className="absolute inset-0 pointer-events-none"
              imgClassName="object-cover h-full w-full"
            />
            <div className="flex flex-col items-start mb-9 relative md:w-[60%] lg:w-[50%]">
              <div
                className={clsx(
                  "[font-size:26px] leading-[31px] text-white font-bold text-center mb-6",
                  "md:[font-size:46px] md:leading-[46px] md:text-left",
                  "lg:[font-size:60px] lg:leading-[60px] lg:mb-8",
                  "xl:[font-size:72px] xl:leading-[72px]",
                  "2xl:[font-size:80px] 2xl:leading-[80px] 2xl:mt-1"
                )}
              >
                <h3>{t("chapters.4.title")}</h3>
                <p>{t("chapters.4.subtitle")}</p>
              </div>
              <CTALink
                href="https://discord.com/invite/RJwgFDtyWe"
                variant="glass"
              >
                {t("chapters.4.cta")}
              </CTALink>
            </div>
          </Section>

          <div
            className={clsx(
              "flex flex-col space-y-5",
              "md:flex-row md:space-y-0 md:space-x-5",
              "lg:space-x-6"
            )}
          >
            {/* Section 6 */}
            <Section
              nodeIndex={1}
              className={clsx(
                "pt-8 bg-about-purple-gradient relative",
                "md:pt-10 md:flex-1",
                "lg:pt-12.5",
                "xl:pt-15"
              )}
            >
              <Picture
                path="/img/home/6"
                className="absolute inset-0 pointer-events-none"
                imgClassName="object-contain h-full w-full"
              />
              <div
                className={clsx(
                  "[font-size:26px] leading-[31px] text-white font-semibold text-center max-w-[328px] relative",
                  "md:[font-size:22px] md:leading-[24px] md:max-w-[250px]",
                  "lg:[font-size:32px] lg:leading-[35px] lg:max-w-[360px]",
                  "xl:[font-size:40px] xl:leading-[44px] xl:max-w-[460px]"
                )}
              >
                <h3 className="font-bold">{t("chapters.5.title")}</h3>
                <p className="font-semibold">{t("chapters.5.subtitle")}</p>
              </div>
            </Section>

            {/* Section 7 */}
            <Section
              nodeIndex={1}
              className={clsx(
                "pt-8 bg-about-purple-gradient relative",
                "md:pt-10 md:flex-1",
                "lg:pt-12.5",
                "xl:pt-15"
              )}
            >
              <Picture
                path="/img/home/7"
                className="absolute inset-0 pointercontain-none"
                imgClassName="object-cover h-full w-full"
              />
              <div
                className={clsx(
                  "[font-size:26px] leading-[31px] text-white font-semibold text-center max-w-[328px] mb-12 relative",
                  "md:[font-size:22px] md:leading-[24px] md:max-w-[250px] md:mb-0",
                  "lg:[font-size:32px] lg:leading-[35px] lg:max-w-[360px]",
                  "xl:[font-size:40px] xl:leading-[44px] xl:max-w-[460px]"
                )}
              >
                <h3 className="font-bold">{t("chapters.6.title")}</h3>
                <p className="font-semibold">{t("chapters.6.subtitle")}</p>
              </div>
            </Section>
          </div>

          {/* Section 8 */}
          <Section
            nodeIndex={2}
            className={clsx(
              "bg-about-magenta-gradient pt-8 px-4.5 relative",
              "md:flex-row md:justify-start md:relative md:pl-10 md:pt-12.5 md:pb-[140px]",
              "lg:pt-[74px] lg:pb-[208px] lg:pl-9 lg:items-start",
              "xl:pt-25 xl:pb-[292px]",
              "2xl:pt-[144px] 2xl:pb-[320px]"
            )}
          >
            <Picture
              path="/img/home/8"
              className="absolute inset-0 pointer-events-none"
              imgClassName="object-cover h-full w-full"
            />
            <div
              className={clsx(
                "flex flex-col items-center max-w-[220px] relative",
                "md:items-start md:max-w-[60%]",
                "xl:max-w-[55%]"
              )}
            >
              <div
                className={clsx(
                  "[font-size:26px] leading-[31px] text-white font-bold text-center mb-6",
                  "md:[font-size:46px] md:leading-[46px] md:text-left",
                  "lg:[font-size:60px] lg:leading-[60px] lg:mb-8",
                  "xl:[font-size:72px] xl:leading-[72px]",
                  "2xl:[font-size:80px] 2xl:leading-[80px] 2xl:mt-1"
                )}
              >
                <h3>{t("chapters.7.title")}</h3>
                <p>{t("chapters.7.subtitle")}</p>
              </div>
              <CTALink href="https://link.numi.net/43wXKot" variant="glass">
                {t("chapters.7.cta")}
              </CTALink>
            </div>
          </Section>

          <div
            className={clsx(
              "flex flex-col space-y-6",
              "md:space-y-0 md:flex-row md:space-x-5"
            )}
          >
            {/* Section 9 */}
            <Section
              nodeIndex={2}
              className={clsx(
                "bg-about-magenta-gradient pt-8 px-4 pb-[372px]",
                "md:flex-[1.43] md:pb-[264px] md:px-9 md:pt-10",
                "lg:px-7 lg:pt-[50px]"
              )}
            >
              <Picture
                path="/img/home/9"
                className="absolute inset-0 pointer-events-none"
                imgClassName="object-cover h-full w-full"
              />
              <div
                className={clsx(
                  "[font-size:26px] leading-[31px] text-white font-semibold text-center max-w-[300px] relative mb-6",
                  "md:[font-size:22px] md:leading-[24px] md:max-w-[320px]",
                  "lg:[font-size:32px] lg:leading-[35px] lg:max-w-[464px] lg:mb-8",
                  "xl:[font-size:40px] xl:leading-[44px] xl:max-w-[574px]"
                )}
              >
                <h3 className="font-bold">{t("chapters.8.title")}</h3>
                <p
                  className="
                  font-semibold"
                >
                  {t("chapters.8.subtitle")}
                </p>
              </div>
              <CTALink href="https://link.numi.net/43wXKot" variant="glass">
                {t("chapters.8.cta")}
              </CTALink>
            </Section>

            {/* Section 10 */}
            <Section
              nodeIndex={2}
              className={clsx(
                "bg-white py-[130px] px-9 justify-center",
                "md:flex-1 md:py-[82px] md:px-7",
                "lg:px-10 lg:h-[546px]"
              )}
            >
              <img src="/img/home/join-discord.svg" alt="" className="mb-6" />
              <h1
                className={clsx(
                  "font-bold gradient-text-join-discord-gradient text-center mb-8 whitespace-pre-wrap ",
                  "[font-size:40px] leading-[44px] tracking-[-1.2px] max-w-[278px]",
                  "md:[font-size:32px] md:leading-[34px] md:tracking-[-0.96px] md:max-w-[222px]",
                  "lg:[font-size:42px] lg:leading-[45px] lg:tracking-[-1.26px] lg:max-w-[276px]",
                  "xl:[font-size:56px] xl:leading-[60px] xl:tracking-[-0.03em] xl:max-w-[389px]"
                )}
              >
                {t("chapters.9.title")}
              </h1>
              <CTALink
                href="https://link.numi.net/3J6orIy"
                variant="purple-white"
              >
                {t("chapters.9.cta")}
              </CTALink>
            </Section>
          </div>
          <Section nodeIndex={3} className="!h-0" />
          <Footer />
        </div>
      </div>
    </BackgroundThemeContextProvider>
  );
}

export default Home;
